import { AvConnection, useAvContext } from "@minervaproject/avsubsystem";
import { useEffect } from "react";

import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import {
  PROFILE_PROPERTIES,
  useUserProperties,
  useUsersProperties,
} from "hooks/use_user_properties";
import { setBandwidthModeForUser } from "shared/reducers/classroom";
import { LocalModalContentType, PageUnloadReasonType, PubsubMessageTypeType } from "shared/types";
import { MonitoringEventTypeType } from "shared/types/monitoring_event";
import { classroomSelectors, localSelectors } from "store";
import { clearPubsubMessage, showLocalModal } from "store/reducers/local";
import { createMonitoringEvent } from "store/reducers/monitoring";
import { BugReporter } from "utils/bug_reporter";
import { redirectToClassEditPage } from "utils/classroom";

function PubsubMessageObserver() {
  const dispatch = useAppDispatch();
  const avConnection = useAvContext();
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const currentUser = useUserProperties(currentUserId, ["bandwidth_mode"]);
  const users = useUsersProperties(PROFILE_PROPERTIES.concat("id", "is_present"));
  const classInfo = useAppSelector(classroomSelectors.selectClassInfo());
  const pubsubMessage = useAppSelector(localSelectors.selectPubsubMessage());

  useEffect(() => {
    if (!pubsubMessage || !classInfo || !currentUserId) return;

    window.flightLogger.log(
      `[PubsubMessageObserver] Received pubsub message (${pubsubMessage.type})`,
    );

    if (
      pubsubMessage.type === PubsubMessageTypeType.FORCE_BANDWIDTH_MODE_CHANGE &&
      pubsubMessage.data.targetUserId === currentUserId
    ) {
      dispatch(
        createMonitoringEvent({
          type: MonitoringEventTypeType.FORCED_BANDWIDTH_MODE_CHANGE,
          userId: currentUserId,
          contextData: {
            oldBwMode: currentUser?.bandwidth_mode,
            newBwMode: pubsubMessage.data.bandwidthMode,
          },
        }),
      );
      dispatch(
        setBandwidthModeForUser({
          userId: currentUserId,
          bandwidthMode: pubsubMessage.data.bandwidthMode,
        }),
      );
      dispatch(clearPubsubMessage());
    }

    // This user has been inactive for too long, let's kick them out
    if (pubsubMessage.type === PubsubMessageTypeType.KICK_USER_OUT_WITH_WARNING) {
      dispatch(showLocalModal({ content: LocalModalContentType.KickOutWarning }));
      dispatch(clearPubsubMessage());
    }

    // This user entered the classroom on another device, let's kick them out
    if (
      pubsubMessage.type === PubsubMessageTypeType.KICK_USER_OUT_WITHOUT_WARNING &&
      pubsubMessage.data.targetUserId === currentUserId
    ) {
      redirectToClassEditPage(
        { classId: classInfo.id, sectionId: classInfo.section.id, courseId: classInfo.course.id },
        pubsubMessage.data.reason,
      );
    }

    // Refresh the target user's AV
    if (
      pubsubMessage.type === PubsubMessageTypeType.REFRESH_AV &&
      pubsubMessage.data.targetUserId === currentUserId
    ) {
      avConnection?.refreshAv(pubsubMessage.data.reason);
      dispatch(clearPubsubMessage());
    }

    // Reload the page if the user is not on the monitoring page
    if (pubsubMessage.type === PubsubMessageTypeType.RELOAD_PAGE) {
      if (
        !window.location.pathname.startsWith("/monitoring") &&
        (!pubsubMessage.data.targetUserId || pubsubMessage.data.targetUserId === currentUserId)
      ) {
        window.pageUnloadReason = pubsubMessage.data.reason || PageUnloadReasonType.UNKNOWN;
        location.reload();
      }
      dispatch(clearPubsubMessage());
    }

    // A bug report was submitted, let's upload everybody's logs to S3
    if (pubsubMessage.type === PubsubMessageTypeType.UPLOAD_LOGS_TO_S3) {
      const { reportTitle = "", message = "", timestamp } = pubsubMessage.data;
      const avStateString =
        JSON.stringify(avConnection?.getAvStateSnapshot(), null, 2) || "No Av Info";
      const bugReporter = new BugReporter(
        reportTitle,
        message,
        [],
        users,
        currentUserId,
        classInfo,
        avStateString,
        dispatch,
        timestamp,
      );

      bugReporter
        .uploadToS3()
        .catch((err) =>
          window.flightLogger.error("[PubsubMessageObserver] Upload to S3 failed", err),
        );
      dispatch(clearPubsubMessage());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pubsubMessage, currentUserId, currentUser?.bandwidth_mode, classInfo, dispatch]);

  return null;
}

export { PubsubMessageObserver };
